"use client";

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { HuntItemCard } from "@/components/hunt-item-card";
import { HuntSubmissionSummaryCard } from "@/components/hunt-submission-summary-card";
import { HuntSubmissionsSection } from "@/components/hunt-submissions-section";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Palette, Gift, ScanSearch, Clock } from "lucide-react";
import { toast } from "sonner";
import { reportError } from "@/lib/report-error";
import { Fireworks } from "@/components/fireworks";
import { FreeSearchCard } from "@/components/free-search-card";
import { ShareInviteCard } from "@/components/share-invite-card";
import { SalesFollowUpCard } from "@/components/sales-follow-up-card";
import { HuntSectionSeparator } from "@/components/hunt-section-separator";
import { UserFooter } from "@/components/user-footer";
import { useHunt } from "@/hooks/use-hunt";
import type { HuntData } from "@/hooks/use-hunt";

interface HuntViewProps {
  userName?: string;
  userEmail?: string;
  userCompany?: string | null;
}

/** Free search, share, and sales follow-up — keep these together in every hunt layout branch. */
function HuntPromoCards({ userEmail }: { userEmail?: string | null }) {
  return (
    <>
      <FreeSearchCard />
      <ShareInviteCard />
      <SalesFollowUpCard userEmail={userEmail} />
    </>
  );
}

function HuntPromoStack({ userEmail }: { userEmail?: string | null }) {
  return (
    <>
      <HuntSectionSeparator icon={Clock} label="While you wait" />
      <HuntPromoCards userEmail={userEmail} />
    </>
  );
}

export function HuntView({ userName, userEmail, userCompany }: HuntViewProps) {
  const { hunt, items, completions, hasEnteredDrawing, isLoading, error, mutate } = useHunt();
  const [showCelebration, setShowCelebration] = useState(false);
  const [prizeDescription, setPrizeDescription] = useState("");

  const hasScrolledToCelebration = useRef(false);

  useEffect(() => {
    if (!showCelebration || hasScrolledToCelebration.current) return;
    hasScrolledToCelebration.current = true;

    // Vaul drawers set position:fixed / overflow:hidden on the body and
    // restore asynchronously via rAF.  Wait for that cleanup to land
    // before scrolling, otherwise the scroll is a no-op on iOS Safari.
    const timer = setTimeout(() => {
      document.body.style.removeProperty("position");
      document.body.style.removeProperty("top");
      document.body.style.removeProperty("left");
      document.body.style.removeProperty("overflow");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 50);
    return () => clearTimeout(timer);
  }, [showCelebration]);

  const handleComplete = useCallback(
    async (itemId: string, payload: { patentId: string; patentCdnPath: string; capturedImageDataUrl: string }) => {
      const { patentId, patentCdnPath, capturedImageDataUrl } = payload;
      const optimisticCompletion = {
        id: `optimistic-${itemId}`,
        hunt_item_id: itemId,
        patent_id: patentId,
        patent_cdn_path: patentCdnPath || null,
        input_image_url: null as string | null,
      };

      try {
        await mutate(
          async (current: HuntData | undefined) => {
            const formData = new FormData();
            formData.append("huntItemId", itemId);
            formData.append("patentId", patentId);
            if (patentCdnPath) formData.append("patentCdnPath", patentCdnPath);

            if (capturedImageDataUrl) {
              const parts = capturedImageDataUrl.split(",");
              if (parts.length === 2 && parts[1]) {
                const mimeMatch = parts[0].match(/:(.*?);/);
                const mimeType = mimeMatch?.[1] ?? "image/jpeg";
                const byteString = atob(parts[1]);
                const ab = new ArrayBuffer(byteString.length);
                const u8 = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) u8[i] = byteString.charCodeAt(i);
                const ext = mimeType.split("/")[1] ?? "jpg";
                formData.append("image", new Blob([u8], { type: mimeType }), `capture.${ext}`);
              }
            }

            const response = await fetch("/api/hunt/complete", {
              method: "POST",
              body: formData,
            });

            if (!response.ok) {
              const errData = await response.json().catch(() => ({}));
              throw new Error(errData.error || "Failed to complete item");
            }

            const result = await response.json();

            if (result.huntCompleted) {
              setPrizeDescription(hunt?.prize_description || "a special prize");
              setShowCelebration(true);
            }

            const serverCompletion = result.completion
              ? {
                  id: result.completion.id,
                  hunt_item_id: itemId,
                  patent_id: patentId,
                  input_image_url: result.completion.input_image_url ?? null,
                  patent_cdn_path: result.completion.patent_cdn_path ?? null,
                }
              : optimisticCompletion;

            return {
              hunt: current?.hunt ?? null,
              items: current?.items ?? [],
              completions: [
                ...(current?.completions ?? []).filter((c) => c.hunt_item_id !== itemId),
                serverCompletion,
              ],
              hasEnteredDrawing: current?.hasEnteredDrawing || result.enteredDrawing || false,
            };
          },
          {
            optimisticData: (current: HuntData | undefined) => ({
              hunt: current?.hunt ?? null,
              items: current?.items ?? [],
              completions: [...(current?.completions ?? []), optimisticCompletion],
              hasEnteredDrawing: current?.hasEnteredDrawing ?? false,
            }),
            rollbackOnError: true,
            revalidate: false,
          }
        );
      } catch (err) {
        reportError(err, { component: "HuntView", action: "handleComplete" });
        const message = err instanceof Error ? err.message : "Failed to complete item";
        toast.error(message);
        throw err;
      }
    },
    [mutate, hunt?.prize_description]
  );

  // Must run unconditionally — hooks cannot follow early returns (React #310 when hunt loads
  // after the loading skeleton render).
  const submissionSummaries = useMemo(() => {
    return items
      .map((item, idx) => {
        const completion = completions.find((c) => c.hunt_item_id === item.id);
        if (!completion) return null;
        return {
          key: item.id,
          index: idx + 1,
          itemName: item.name,
          patentId: completion.patent_id,
          inputImageUrl: completion.input_image_url,
          patentCdnPath: completion.patent_cdn_path,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null);
  }, [items, completions]);

  if (isLoading && !hunt && items.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-48 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <HuntPromoStack userEmail={userEmail} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <Card className="rounded-2xl border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load hunt data. Please try again.</p>
          </CardContent>
        </Card>
        <HuntPromoStack userEmail={userEmail} />
      </div>
    );
  }

  if (!hunt) {
    return (
      <div className="flex flex-col gap-4">
        <section className="-mx-4 -mt-5 px-10 pb-5 pt-5">
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/15 shadow-sm">
              <svg className="h-6 w-6 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="12" x2="12" y2="7" className="origin-[12px_12px] animate-[spin_10s_linear_infinite]" />
                <line x1="12" y1="12" x2="16" y2="12" className="origin-[12px_12px] animate-[spin_60s_linear_infinite]" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold">No Active Hunt</h2>
            <p className="mt-1 text-base text-muted-foreground">
              Check back later for the next hunt.
            </p>
          </div>
        </section>

        <HuntPromoStack userEmail={userEmail} />
        {userName && userEmail && <UserFooter name={userName} email={userEmail} company={userCompany} />}
      </div>
    );
  }

  const completedCount = completions.length;
  const totalCount = items.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const isHuntComplete = completedCount >= totalCount && totalCount > 0;

  const getCompletionForItem = (itemId: string) => {
    return completions.find((c) => c.hunt_item_id === itemId);
  };

  if (showCelebration || (isHuntComplete && hasEnteredDrawing)) {
    return (
      <div className="flex flex-col gap-4">
        <section className="-mx-4 -mt-5 px-10 pb-5 pt-5">
          <div className="relative flex flex-col items-center justify-center py-4 text-center">
            <Fireworks />
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-500 shadow-sm">
              <Trophy className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-green-700 dark:text-green-400">
              Hooray!
            </h2>
            <p className="mt-1 text-base text-muted-foreground">
              You&apos;ve been entered in the drawing for{" "}
              <span className="font-semibold">{prizeDescription || hunt.prize_description}</span>.
              <br />
              Check back tomorrow for another hunt!
            </p>
          </div>
        </section>

        <HuntPromoStack userEmail={userEmail} />

        {submissionSummaries.length > 0 && (
          <HuntSubmissionsSection count={submissionSummaries.length}>
            {submissionSummaries.map((row) => (
              <HuntSubmissionSummaryCard
                key={row.key}
                index={row.index}
                itemName={row.itemName}
                patentId={row.patentId}
                inputImageUrl={row.inputImageUrl}
                patentCdnPath={row.patentCdnPath}
              />
            ))}
          </HuntSubmissionsSection>
        )}
        {userName && userEmail && <UserFooter name={userName} email={userEmail} company={userCompany} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-4">
        <div className="grid grid-cols-[auto_auto_1fr] items-center gap-x-2 gap-y-1.5">
          <Palette className="h-5 w-5 text-primary" />
          <span className="text-base text-muted-foreground">Theme</span>
          <span className="text-base font-semibold">{hunt.name}</span>
          <Gift className="h-5 w-5 text-primary" />
          <span className="text-base text-muted-foreground">Prize</span>
          <span className="text-base font-semibold">{hunt.prize_description}</span>
        </div>
        
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-primary px-3 py-0.5 text-sm font-semibold text-primary-foreground">
                Day {hunt.day_number}
              </span>
            </div>
            <span className="text-base font-semibold">
              {completedCount} / {totalCount} items
            </span>
          </div>
          <Progress value={progress} className="rounded-full" />
        </div>
      </section>

      <HuntSectionSeparator icon={ScanSearch} label="Find these items" labelTone="muted" />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {items.map((item, idx) => {
          const completion = getCompletionForItem(item.id);
          return (
            <HuntItemCard
              key={item.id}
              item={item}
              index={idx + 1}
              isCompleted={!!completion}
              patentId={completion?.patent_id}
              inputImageUrl={completion?.input_image_url}
              patentCdnPath={completion?.patent_cdn_path}
              huntId={hunt.id}
              onComplete={handleComplete}
            />
          );
        })}
      </div>

      <HuntPromoStack userEmail={userEmail} />

      {userName && userEmail && <UserFooter name={userName} email={userEmail} company={userCompany} />}
    </div>
  );
}
